import React, { useState, useEffect, useRef } from "react";
import "./PaymentSetup.scss"; // Import SCSS file
import axiosInstance from "../../../axiosInstance";
import PaymentSetupContent from "./PaymentSetupContent"; // Import PaymentSetupContent component
import { toast } from "react-toastify";
import IconButton from "../../../components/IconButton/IconButton";
import { Search } from "lucide-react";
import "react-toastify/dist/ReactToastify.css";
import ColoredCheckbox from "../../../components/ColoredCheckbox/ColoredCheckbox";
import ButtonWithIcon from "../../../components/ButtonwithIcon/ButtonWithIcon";
import WarningPopup from "../../../components/WarningPopup/WarningPopup";

const initialPaymentSetups = [
  {
    id: 1,
    salaryType: "EARNING",
    salaryName: "Basic",
    contributionType: "EMPLOYEE",
    value: "60",
    valueType: "PERCENTAGE",
    limitAmount: 0,
    paymentCalculationBasedOn: "GROSS_SALARY",
    taxType: "NIL",
    fixedSetup: true,
    remainingEarnings: false,
  },
  {
    id: 2,
    salaryType: "EARNING",
    salaryName: "HRA",
    contributionType: "EMPLOYEE",
    value: "40",
    valueType: "PERCENTAGE",
    limitAmount: 0,
    paymentCalculationBasedOn: "PAYMENT_SETUP",
    paymentSetupBasedOnNames: ["Basic"],
    taxType: "NIL",
    fixedSetup: true,
    remainingEarnings: false,
  },
  {
    id: 12,
    salaryType: "EARNING",
    salaryName: "Conveyance",
    contributionType: "EMPLOYEE",
    value: "1650",
    valueType: "FIXED",
    limitAmount: 0,
    paymentCalculationBasedOn: "GROSS_SALARY",
    taxType: "NIL",
    fixedSetup: true,
    remainingEarnings: false,
  },
  {
    id: 3,
    salaryType: "EARNING",
    salaryName: "Special Allowance",
    contributionType: "EMPLOYEE",
    value: "0",
    valueType: "FIXED",
    remainingEarnings: true,
    limitAmount: 0,
    paymentCalculationBasedOn: "GROSS_SALARY",
    taxType: "NIL",
    fixedSetup: true,
  },
  {
    id: 4,
    salaryType: "CONTRIBUTION",
    salaryName: "ESI",
    contributionType: "EMPLOYEE",
    value: "0.75",
    limitAmount: 21000,
    valueType: "PERCENTAGE",
    paymentCalculationBasedOn: "GROSS_SALARY",
    taxType: "ESI",
    fixedSetup: true,
    remainingEarnings: false,
  },
  {
    id: 7,
    salaryType: "CONTRIBUTION",
    salaryName: "PF",
    contributionType: "EMPLOYEE",
    value: "12",
    limitAmount: 1800,
    valueType: "PERCENTAGE",
    paymentCalculationBasedOn: "PAYMENT_SETUP",
    paymentSetupBasedOnNames: ["Basic"],
    taxType: "PF",
    fixedSetup: true,
    remainingEarnings: false,
  },
  {
    id: 10,
    salaryType: "CONTRIBUTION",
    salaryName: "PT",
    contributionType: "EMPLOYEE",
    value: "0",
    valueType: "FIXED",
    limitAmount: 0,
    paymentCalculationBasedOn: "GROSS_SALARY",
    taxType: "PT",
    fixedSetup: true,
    remainingEarnings: false,
  },
  {
    id: 8,
    salaryType: "CONTRIBUTION",
    salaryName: "IT",
    contributionType: "EMPLOYEE",
    value: "0",
    valueType: "FIXED",
    limitAmount: 0,
    paymentCalculationBasedOn: "GROSS_SALARY",
    taxType: "IT",
    fixedSetup: true,
    remainingEarnings: false,
  },
  {
    id: 5,
    salaryType: "CONTRIBUTION",
    salaryName: "ESI",
    contributionType: "EMPLOYER",
    value: "2.25",
    limitAmount: 21000,
    valueType: "PERCENTAGE",
    paymentCalculationBasedOn: "GROSS_SALARY",
    taxType: "ESI",
    fixedSetup: true,
    remainingEarnings: false,
  },
  {
    id: 6,
    salaryType: "CONTRIBUTION",
    salaryName: "PF",
    contributionType: "EMPLOYER",
    value: "12",
    limitAmount: 1800,
    valueType: "PERCENTAGE",
    paymentCalculationBasedOn: "PAYMENT_SETUP",
    paymentSetupBasedOnNames: ["Basic"],
    taxType: "PF",
    fixedSetup: true,
    remainingEarnings: false,
  },
  {
    id: 11,
    salaryType: "CONTRIBUTION",
    salaryName: "EPS + EDLI",
    contributionType: "EMPLOYER",
    value: "1.5",
    valueType: "PERCENTAGE",
    limitAmount: 150,
    paymentCalculationBasedOn: "PAYMENT_SETUP",
    paymentSetupBasedOnNames: ["Basic"],
    taxType: "NIL",
    fixedSetup: true,
    remainingEarnings: false,
  },
];

function PaymentSetup() {
  const [payrollType, setPayrollType] = useState("AUTO"); // Default to Manual
  const formRef = useRef(null);
  const [payrollName, setPayrollName] = useState("");
  const [payrolls, setPayrolls] = useState([]);
  const [selectedPayrollId, setSelectedPayrollId] = useState(null);
  const [isAddingPayroll, setIsAddingPayroll] = useState(true);
  const [manualFormData, setManualFormData] = useState([
    { salaryType: "", salaryName: "", contributionType: "" },
  ]);
  const [editingSetupId, setEditingSetupId] = useState(null); // Track the setup being edited
  const [autoFormData, setAutoFormData] = useState({
    salaryName: "",
    salaryType: "",
    contributionType: "",
    limitAmount: "",
    value: "",
    valueType: "",
    paymentCalculationBasedOn: "GROSS_SALARY", // Default value
    paymentSetup: [], // selected "based on" setup names (multi-select)
    predefinedSetup: "",
    remainingEarnings: false, // Added remainingEarnings property
    taxType: "NIL", // Added taxType property with default value
  });
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [templates, setTemplates] = useState([]); // State for storing templates
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [deletePopup, setDeletePopup] = useState({
    isOpen: false,
    payrollToDelete: null,
  });

  useEffect(() => {
    // Define an async function to fetch the templates
    const fetchTemplates = async () => {
      try {
        const res = await axiosInstance.get(
          `${process.env.REACT_APP_API_BASE_URL}/payslip/image-previews`,
        );
        setTemplates(res.data.data);
      } catch (err) {
        console.error("Failed to fetch templates", err);
      }
    };

    fetchTemplates();
  }, []); // Empty dependency array to run only once when the component mounts

  const fetchPayrolls = async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_BASE_URL}/payroll/names`,
      );

      // Check if response.data.data exists and is an array
      if (response.data && Array.isArray(response.data.data)) {
        setPayrolls(response.data.data);
      } else {
        console.warn("Unexpected API response structure:", response.data);
        setPayrolls([]);
      }
    } catch (error) {
      console.error("Failed to fetch payrolls", error);
      setPayrolls([]); // Ensure it's always an array
    }
  };

  // Fetch payrolls
  useEffect(() => {
    fetchPayrolls();
  }, []);

  // 1. Add this helper function at the top of your component (after imports)
  const capitalizeFirst = (str) => {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const [predefinedSetups, setPredefinedSetups] =
    useState(initialPaymentSetups);

  // Check if any setup has remainingEarnings set to true
  const hasRemainingEarningsSetup = predefinedSetups.some(
    (setup) => setup.remainingEarnings === true,
  );

  // API expects an array of names, and only when the calculation base is PAYMENT_SETUP
  // Unique local key for a new setup (length + 1 collides with seeded ids like 12)
  const getNextLocalId = () =>
    predefinedSetups.reduce((max, s) => Math.max(max, Number(s.id) || 0), 0) +
    1;

  // Remaining Earnings only applies to EARNING setups (checkbox is hidden otherwise)
  const getRemainingEarnings = (form) =>
    form.salaryType?.toUpperCase() === "EARNING" &&
    form.taxType !== "PT" &&
    !!form.remainingEarnings;

  const getBasedOnNames = (form) =>
    form.paymentCalculationBasedOn === "PAYMENT_SETUP" &&
    form.valueType?.toUpperCase() === "PERCENTAGE" &&
    form.paymentSetup?.length
      ? form.paymentSetup
      : [];

  const handlePayrollTypeChange = (event) => {
    setPayrollType(event.target.value);
  };

  const handlePayrollNameChange = (event) => {
    setPayrollName(event.target.value);
  };

  const handleAutoChange = (e) => {
    const { name, value } = e.target;

    // Handle checkbox for remainingEarnings
    if (name === "remainingEarnings") {
      setAutoFormData((prev) => ({
        ...prev,
        [name]: e.target.checked,
      }));
    } else if (
      name === "paymentCalculationBasedOn" &&
      value !== "PAYMENT_SETUP"
    ) {
      // Not based on other setups -> drop any ticked predefined setups
      setAutoFormData((prev) => ({
        ...prev,
        [name]: value,
        paymentSetup: [],
      }));
    } else {
      setAutoFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleTogglePaymentSetup = (name) => {
    setAutoFormData((prev) => {
      const current = prev.paymentSetup || [];
      const next = current.includes(name)
        ? current.filter((n) => n !== name)
        : [...current, name];
      return {
        ...prev,
        paymentSetup: next,
        // ticking a setup means the calculation is based on Payment Setup
        paymentCalculationBasedOn: next.length
          ? "PAYMENT_SETUP"
          : prev.paymentCalculationBasedOn,
      };
    });
  };

  const handleManualChange = (field, value) => {
    const updatedData = { ...manualFormData[0], [field]: value };
    setManualFormData([updatedData]);
  };

  const handleDeletePaymentSetup = (id) => {
    setPredefinedSetups((prev) => {
      const removed = prev.find((setup) => setup.id === id);
      return prev
        .filter((setup) => setup.id !== id)
        .map((setup) => ({
          ...setup,
          paymentSetupBasedOnNames: (
            setup.paymentSetupBasedOnNames || []
          ).filter(
            (name) =>
              name !== removed?.salaryName ||
              prev.some((s) => s.id !== id && s.salaryName === name),
          ),
        }));
    });
  };

  const handleEditPaymentSetup = (setup) => {
    setEditingSetupId(setup.id);
    setPayrollType("AUTO"); // Set the form to automatic for now
    setAutoFormData({
      salaryName: setup.salaryName,
      salaryType: setup.salaryType,
      contributionType: setup.contributionType,
      limitAmount: setup.limitAmount,
      value: setup.value,
      valueType: setup.valueType,
      paymentSetup: (setup.paymentSetupBasedOnNames || []).filter((name) =>
        predefinedSetups.some((s) => s.salaryName === name),
      ),
      paymentCalculationBasedOn:
        setup.paymentCalculationBasedOn || "GROSS_SALARY",
      remainingEarnings: setup.remainingEarnings || false,
      taxType: setup.taxType || "NIL", // Include taxType when editing
    });

    // Scroll to the form
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Enhanced handleAddOrUpdatePaymentSetup function with proper validation
  const handleAddOrUpdatePaymentSetup = (e) => {
    if (e) e.preventDefault(); // Prevent any potential form submission
    e.stopPropagation(); // Add this to prevent event bubbling

    // Validate based on payroll type
    if (payrollType === "MANUAL") {
      // Validation for manual form
      const { salaryType, salaryName, contributionType } = manualFormData[0];

      if (!salaryType || !salaryName || !contributionType) {
        toast.warn("Please fill all mandatory fields in the form.");
        return;
      }
    } else if (payrollType === "AUTO") {
      // Validation for automatic form
      const { salaryName, salaryType, contributionType, value, valueType } =
        autoFormData;

      // Check required fields
      if (
        !salaryName ||
        !salaryType ||
        !contributionType ||
        !value ||
        !valueType
      ) {
        toast.warn("Please fill all mandatory fields in the form.");
        return;
      }

      // Validate percentage-specific fields
      if (valueType === "PERCENTAGE") {
        const { paymentCalculationBasedOn } = autoFormData;

        if (!paymentCalculationBasedOn) {
          toast.warn(
            "Please select Payment Based On for percentage value type.",
          );
          return;
        }

        // Validate paymentSetup if PAYMENT_SETUP is selected
        if (
          paymentCalculationBasedOn === "PAYMENT_SETUP" &&
          !autoFormData.paymentSetup?.length
        ) {
          toast.warn(
            "Please select at least one Predefined Setup when Payment Based On is set to Payment Setup.",
          );
          return;
        }
      }
    }

    // For remainingEarnings: if it's checked and another setup already has it as true, show warning
    if (
      getRemainingEarnings(autoFormData) &&
      hasRemainingEarningsSetup &&
      (editingSetupId === null ||
        !predefinedSetups.find((s) => s.id === editingSetupId)
          ?.remainingEarnings)
    ) {
      toast.warn(
        "Only one payment setup can have 'Remaining Earnings' enabled.",
      );
      return;
    }

    if (editingSetupId) {
      // Update the existing setup
      setPredefinedSetups((prev) =>
        prev.map((setup) =>
          setup.id === editingSetupId
            ? {
                ...setup,
                salaryName: autoFormData.salaryName,
                salaryType: autoFormData.salaryType.toUpperCase(),
                contributionType: autoFormData.contributionType.toUpperCase(),
                value: autoFormData.value,
                valueType: autoFormData.valueType.toUpperCase(),
                limitAmount: autoFormData.limitAmount || 0,
                paymentCalculationBasedOn:
                  autoFormData.paymentCalculationBasedOn || "GROSS_SALARY",
                paymentSetupBasedOnNames: getBasedOnNames(autoFormData),
                remainingEarnings: getRemainingEarnings(autoFormData),
                taxType: autoFormData.taxType || "NIL",
              }
            : setup,
        ),
      );
      setEditingSetupId(null); // Clear editing mode
      toast.success("Payment setup updated successfully!");
    } else {
      // Add a new setup
      const newSetup =
        payrollType === "MANUAL"
          ? {
              id: getNextLocalId(),
              isCustom: true,
              salaryType: manualFormData[0].salaryType.toUpperCase(),
              salaryName: manualFormData[0].salaryName,
              contributionType:
                manualFormData[0].contributionType.toUpperCase(),
              value: "",
              valueType: "",
              limitAmount: 0,
              paymentCalculationBasedOn: "",
              taxType: "NIL",
              fixedSetup: false,
              remainingEarnings: false, // FIX: Always false for new manual setups
            }
          : {
              id: getNextLocalId(),
              isCustom: true,
              salaryType: autoFormData.salaryType.toUpperCase(),
              salaryName: autoFormData.salaryName,
              contributionType: autoFormData.contributionType.toUpperCase(),
              value: autoFormData.value,
              valueType: autoFormData.valueType.toUpperCase(),
              limitAmount: autoFormData.limitAmount || 0,
              paymentCalculationBasedOn:
                autoFormData.paymentCalculationBasedOn || "GROSS_SALARY",
              paymentSetupBasedOnNames: getBasedOnNames(autoFormData),
              fixedSetup: false,
              remainingEarnings: getRemainingEarnings(autoFormData),
              taxType: autoFormData.taxType || "NIL",
            };

      setPredefinedSetups((prev) => [...prev, newSetup]);
      toast.success("Payment setup added successfully!");
    }

    // Clear form data
    setAutoFormData({
      salaryName: "",
      salaryType: "",
      contributionType: "",
      limitAmount: "",
      value: "",
      valueType: "",
      paymentCalculationBasedOn: "GROSS_SALARY",
      paymentSetup: [],
      remainingEarnings: false,
      taxType: "NIL",
    });

    // Reset manual form data
    setManualFormData([
      { salaryType: "", salaryName: "", contributionType: "" },
    ]);

    // Return false to prevent any form submission
    return false;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (editingSetupId) {
      return;
    }

    // Validate payroll name
    if (!payrollName.trim()) {
      toast.error("Payroll Name is required.");
      return;
    }

    // Check if at least one payment setup is added
    if (predefinedSetups.length === 0) {
      toast.error("Please add at least one payment setup before submitting.");
      return;
    }

    // Validate template selection
    if (!selectedTemplateId) {
      toast.error("Please select a payslip template.");
      return;
    }

    // Prepare payroll data
    const payrollData = {
      payrollName,
      paymentSetups: predefinedSetups.map((setup) => {
        const existingNames = predefinedSetups.map((s) => s.salaryName);
        const basedOnNames =
          setup.paymentCalculationBasedOn === "PAYMENT_SETUP" &&
          setup.valueType === "PERCENTAGE"
            ? [...new Set(setup.paymentSetupBasedOnNames || [])].filter(
                (name) =>
                  name !== setup.salaryName && existingNames.includes(name),
              )
            : [];
        let setupData = {
          // custom (newly added) setups have no backend id yet
          ...(setup.isCustom ? {} : { paymentSetupId: setup.id }),
          salaryType: setup.salaryType,
          salaryName: setup.salaryName,
          contributionType: setup.contributionType,
          limitAmount: Number(setup.limitAmount) || 0,
          status: "ENABLED",
          paymentCalculationBasedOn:
            setup.paymentCalculationBasedOn || "GROSS_SALARY",
          paymentSetupBasedOnNames: basedOnNames,
          taxType: setup.taxType,
          remainingEarnings:
            setup.salaryType === "EARNING" && !!setup.remainingEarnings,
          fixedSetup: setup.fixedSetup,
        };
        setupData.value = setup.value !== "" ? Number(setup.value) : 0;
        setupData.valueType =
          setup.valueType !== "" ? setup.valueType : "FIXED";

        return setupData;
      }),
      payrollSetupType: payrollType,
      companyId: parseInt(process.env.REACT_APP_COMPANY_ID),
      payslipTemplateId: selectedTemplateId,
    };

    console.log(
      "create payroll payload:",
      JSON.stringify(payrollData, null, 2),
    );

    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_BASE_URL}/payroll/create`,
        payrollData,
      );

      if (response.status === 200) {
        toast.success("Payroll successfully created!");

        // Reset form after successful submission
        setPayrollName("");
        // Reset to original predefined setups instead of empty array
        setPredefinedSetups(initialPaymentSetups);
        setSelectedTemplateId(null);

        // Fetch updated payroll list after successful submission
        fetchPayrolls();

        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
    } catch (error) {
      console.error(
        "Create payroll failed:",
        error.response?.data || error.message,
      );
      toast.error(error.response?.data?.message || "Failed to create payroll.");
    }
  };

  const handleSelectPayroll = (id) => {
    setSelectedPayrollId(id);
    setIsAddingPayroll(false);
  };

  // 3. Update the handleAddPayroll function (this remains the same but for clarity)
  const handleAddPayroll = () => {
    setIsAddingPayroll(true);
    setSelectedPayrollId(null);
  };

  const handleCancelForm = () => {
    setAutoFormData({
      salaryName: "",
      salaryType: "",
      contributionType: "",
      limitAmount: "",
      value: "",
      valueType: "",
      paymentCalculationBasedOn: "GROSS_SALARY",
      paymentSetup: [],
      predefinedSetup: "",
      remainingEarnings: false,
      taxType: "NIL",
    });
    setManualFormData([
      { salaryType: "", salaryName: "", contributionType: "" },
    ]);
    setEditingSetupId(null);
  };

  const handleDeleteClick = (e, payroll) => {
    e.stopPropagation(); // Prevent card selection when clicking delete
    setDeletePopup({
      isOpen: true,
      payrollToDelete: payroll,
    });
  };

  const handleDeleteConfirm = async () => {
    const { payrollToDelete } = deletePopup;

    if (!payrollToDelete) {
      // If there's no payroll to delete, do nothing
      setDeletePopup({ isOpen: false, payrollToDelete: null });
      return;
    }

    try {
      // axios will throw an error for non-2xx status codes
      await axiosInstance.delete(
        `${process.env.REACT_APP_API_BASE_URL}/Employee-Payroll/${payrollToDelete.payrollId}`,
      );

      // If the request succeeds, we land here
      toast.success("Payroll deleted successfully!");

      // As you asked, call the payroll types again
      fetchPayrolls();

      // Clear selection if the deleted payroll was the one selected
      if (selectedPayrollId === payrollToDelete.payrollId) {
        setSelectedPayrollId(null);
      }
    } catch (error) {
      console.error("Error deleting payroll:", error);
    } finally {
      // Always close the popup, regardless of success or failure
      setDeletePopup({ isOpen: false, payrollToDelete: null });
    }
  };

  const handleDeleteCancel = () => {
    setDeletePopup({ isOpen: false, payrollToDelete: null });
  };

  return (
    <div className="payment-setup">
      {/* Page Header */}
      <div className="page-header">
        <h1 className="display6">Payroll Management</h1>
        <p className="page-subtitle">
          Create and manage your payroll setups efficiently with advanced
          configuration options
        </p>
        <div className="header-decoration">
          <div className="decoration-circle"></div>
          <div className="decoration-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </div>

      {/* Payroll Selection Section */}
      <div className="payroll-selection-section">
        <div className="section-card">
          <div className="card-header">
            <div className="header-left">
              <h3 className="section-title">
                <span className="title-icon">📋</span>
                Existing Payrolls
              </h3>
              <p className="section-subtitle">
                Select an existing payroll or create a new one to get started
              </p>
            </div>
            <div className="header-stats">
              <div className="stat-item">
                <span className="stat-number">{payrolls.length}</span>
                <span className="stat-label">Active Payrolls</span>
              </div>
            </div>
          </div>

          <div className="payroll-grid">
            {payrolls.map((payroll, index) => (
              <div
                key={payroll.payrollId || index}
                className={`payroll-card ${
                  selectedPayrollId === (payroll.payrollId || index)
                    ? "selected"
                    : ""
                }`}
                onClick={() => handleSelectPayroll(payroll.payrollId || index)}
              >
                <div className="payroll-card-actions">
                  <IconButton
                    variant="delete"
                    icon="mdi:delete-outline"
                    onClick={(e) => handleDeleteClick(e, payroll)}
                    className="delete-button"
                  />
                </div>
                <div className="payroll-info">
                  <h4 className="payroll-name">
                    {payroll.payrollName || "Unnamed Payroll"}
                  </h4>
                  {payroll.payrollSetupType && (
                    <span
                      className={`payroll-type-badge ${payroll.payrollSetupType.toLowerCase()}`}
                    >
                      <span className="badge-icon">
                        {payroll.payrollSetupType === "AUTO" ? "⚡" : "✋"}
                      </span>
                      {payroll.payrollSetupType}
                    </span>
                  )}
                </div>
              </div>
            ))}

            <div
              className={`add-payroll-card ${isAddingPayroll ? "active" : ""}`}
              onClick={handleAddPayroll}
            >
              <div className="add-content">
                <div className="add-text-content">
                  <span className="add-title">Create New Payroll</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {selectedPayrollId ? (
        <PaymentSetupContent payrollId={selectedPayrollId} />
      ) : (
        <div className="new-payroll-container">
          <form onSubmit={handleSubmit} className="payroll-form">
            {/* Basic Information Card */}
            <div className="form-section">
              <div className="section-card">
                <div className="card-header">
                  <div className="header-left">
                    <h3 className="section-title">
                      <span className="title-icon">⚙️</span>
                      Basic Configuration
                    </h3>
                    <p className="section-subtitle">
                      Set up the fundamental details and operational mode of
                      your payroll system
                    </p>
                  </div>
                  <div className="progress-indicator">
                    <span className="step active">1</span>
                    <span className="step-label">Basic Setup</span>
                  </div>
                </div>

                <div className="card-content">
                  <div className="form-grid two-column">
                    <div className="form-group">
                      <label htmlFor="payrollName" className="form-label">
                        <span className="label-text">Payroll Name</span>
                        <span className="required">*</span>
                      </label>
                      <div className="input-wrapper">
                        <input
                          type="text"
                          id="payrollName"
                          className="form-input"
                          value={payrollName}
                          onChange={handlePayrollNameChange}
                          placeholder="Enter a descriptive payroll name"
                        />
                        <div className="input-decoration"></div>
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        <span className="label-text">
                          Payroll Processing Mode
                        </span>
                        <span className="required">*</span>
                      </label>
                      <div className="toggle-group">
                        <button
                          type="button"
                          onClick={() =>
                            handlePayrollTypeChange({
                              target: { value: "AUTO" },
                            })
                          }
                          className={`toggle-btn ${
                            payrollType === "AUTO" ? "active" : ""
                          }`}
                        >
                          <span className="toggle-icon">⚡</span>
                          <div className="toggle-content">
                            <span className="toggle-title">Automatic</span>
                            <span className="toggle-subtitle">
                              Advanced calculations
                            </span>
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            handlePayrollTypeChange({
                              target: { value: "MANUAL" },
                            })
                          }
                          className={`toggle-btn ${
                            payrollType === "MANUAL" ? "active" : ""
                          }`}
                        >
                          <span className="toggle-icon">✋</span>
                          <div className="toggle-content">
                            <span className="toggle-title">Manual</span>
                            <span className="toggle-subtitle">
                              Simple setup
                            </span>
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Template Selection Card */}
            <div className="form-section">
              <div className="section-card">
                <div className="card-header">
                  <div className="header-left">
                    <h3 className="section-title">
                      <span className="title-icon">🎨</span>
                      Payslip Template Selection
                      <span className="required">*</span>
                    </h3>
                    <p className="section-subtitle">
                      Choose a professional template design for your employee
                      payslips
                    </p>
                  </div>
                  <div className="progress-indicator">
                    <span className="step active">2</span>
                    <span className="step-label">Template</span>
                  </div>
                </div>

                <div className="card-content">
                  <div className="templates-grid">
                    {templates.map((template) => (
                      <div
                        key={template.payslipTemplateId}
                        className={`template-card ${
                          selectedTemplateId === template.payslipTemplateId
                            ? "selected"
                            : ""
                        }`}
                        onClick={() =>
                          setSelectedTemplateId(template.payslipTemplateId)
                        }
                      >
                        <div className="template-preview">
                          <img
                            src={`data:image/png;base64,${template.base64}`}
                            alt={template.fileName}
                            className="template-image"
                          />
                          <div className="template-overlay">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEnlargedImage(template.base64);
                              }}
                              className="preview-btn"
                              title="Preview Template"
                            >
                              <Search size={20} />
                            </button>
                          </div>
                          {selectedTemplateId ===
                            template.payslipTemplateId && (
                            <div className="selected-indicator">
                              <span className="selected-check">✓</span>
                            </div>
                          )}
                        </div>
                        <div className="template-info">
                          <h4 className="template-name">{template.fileName}</h4>
                          <span className="template-type">
                            Professional Layout
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Template Preview Modal */}
                  {enlargedImage && (
                    <div
                      className="template-modal"
                      onClick={() => setEnlargedImage(null)}
                    >
                      <div
                        className="modal-content"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          onClick={() => setEnlargedImage(null)}
                          className="modal-close"
                          title="Close Preview"
                        >
                          ✕
                        </button>
                        <div className="modal-image-container">
                          <img
                            src={`data:image/png;base64,${enlargedImage}`}
                            alt="Template Preview"
                            className="modal-image"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Payment Setup Form Card */}
            <div className="form-section" ref={formRef}>
              <div className="section-card">
                {/* <div className="card-header">
                  <div className="header-left">
                    <h3 className="section-title">
                      <span className="title-icon">💰</span>
                      {editingSetupId
                        ? "Edit Payment Setup"
                        : "Add Payment Setup"}
                    </h3>
                    <p className="section-subtitle">
                      {payrollType === "MANUAL"
                        ? "Configure basic payment components with essential parameters"
                        : "Set up detailed payment calculations, rules, and advanced configurations"}
                    </p>
                  </div>
                  <div className="progress-indicator">
                    <span className="step active">3</span>
                    <span className="step-label">Payment Config</span>
                  </div>
                </div> */}

                {/* Payment Setups Table Card */}
                {predefinedSetups.length > 0 && (
                  <div className="form-section">
                    <div className="section-card">
                      <div className="card-header">
                        <div className="header-left">
                          <h3 className="section-title">
                            <span className="title-icon">📊</span>
                            Configured Payment Setups
                          </h3>
                          <p className="section-subtitle">
                            Review and manage your payment configuration
                            components
                          </p>
                        </div>
                        <div className="setup-stats">
                          <div className="stat-group">
                            <div className="stat-item">
                              <span className="stat-number">
                                {predefinedSetups.length}
                              </span>
                              <span className="stat-label">Total Setups</span>
                            </div>
                            <div className="stat-divider"></div>
                            <div className="stat-item">
                              <span className="stat-number success">
                                {
                                  predefinedSetups.filter(
                                    (s) => s.salaryType === "EARNING",
                                  ).length
                                }
                              </span>
                              <span className="stat-label">Earnings</span>
                            </div>
                            <div className="stat-divider"></div>
                            <div className="stat-item">
                              <span className="stat-number danger">
                                {
                                  predefinedSetups.filter(
                                    (s) => s.salaryType === "CONTRIBUTION",
                                  ).length
                                }
                              </span>
                              <span className="stat-label">Contribution</span>
                            </div>
                          </div>
                          <div className="progress-indicator">
                            <span className="step active">3</span>
                            <span className="step-label">Payment Config</span>
                          </div>
                        </div>
                      </div>

                      <div className="card-content">
                        <div className="table-container">
                          <table className="setups-table">
                            <thead>
                              <tr>
                                <th>Component Details</th>
                                <th>Type & Contributor</th>
                                {payrollType === "AUTO" && (
                                  <>
                                    <th>Value Configuration</th>
                                    <th>Calculation Base</th>
                                    <th>Tax Category</th>
                                    <th>Remaining Earnings</th>
                                  </>
                                )}
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {predefinedSetups.map((setup) => (
                                <tr key={setup.id} className="setup-row">
                                  <td>
                                    <div className="setup-details">
                                      <div className="setup-name">
                                        {setup.salaryName}
                                      </div>
                                    </div>
                                  </td>
                                  <td>
                                    <div className="type-info">
                                      <span
                                        className={`type-badge ${setup.salaryType.toLowerCase()}`}
                                      >
                                        {setup.salaryType === "EARNING"
                                          ? "💰"
                                          : "➖"}{" "}
                                        {capitalizeFirst(setup.salaryType)}
                                      </span>
                                      <span className="contribution-type">
                                        {setup.contributionType === "EMPLOYEE"
                                          ? "👤"
                                          : "🏢"}{" "}
                                        {capitalizeFirst(
                                          setup.contributionType,
                                        )}
                                      </span>
                                    </div>
                                  </td>
                                  {payrollType === "AUTO" && (
                                    <>
                                      <td>
                                        <div className="value-config">
                                          <span className="value">
                                            {setup.value}
                                          </span>
                                          <span className="value-type">
                                            {setup.valueType === "PERCENTAGE"
                                              ? "%"
                                              : "₹"}
                                          </span>
                                        </div>
                                      </td>
                                      <td>
                                        <span className="base-info">
                                          {setup.paymentSetupBasedOnNames
                                            ?.length
                                            ? setup.paymentSetupBasedOnNames.join(
                                                ", ",
                                              )
                                            : "Gross Salary"}
                                        </span>
                                      </td>
                                      <td>
                                        <span
                                          className={`tax-badge ${
                                            setup.taxType?.toLowerCase() ||
                                            "nil"
                                          }`}
                                        >
                                          {setup.taxType || "NIL"}
                                        </span>
                                      </td>
                                      <td>
                                        {setup.remainingEarnings ? (
                                          <span className="remaining-badge">
                                            <span className="badge-icon">
                                              🔄
                                            </span>
                                            Yes
                                          </span>
                                        ) : (
                                          <span style={{ opacity: 0.6 }}>
                                            No
                                          </span>
                                        )}
                                      </td>
                                    </>
                                  )}
                                  <td>
                                    <div className="action-buttons">
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleEditPaymentSetup(setup)
                                        }
                                        className="action-btn edit-btn"
                                        title="Edit Setup"
                                      >
                                        <IconButton
                                          icon="iconamoon:edit-light"
                                          variant="edit"
                                        />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleDeletePaymentSetup(setup.id)
                                        }
                                        className="action-btn delete-btn"
                                        title="Delete Setup"
                                      >
                                        <IconButton
                                          icon="fluent:delete-28-regular"
                                          variant="delete"
                                        />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="card-content">
                  {payrollType === "MANUAL" ? (
                    <div className="manual-form-container">
                      <div className="form-grid three-column">
                        <div className="form-group">
                          <label className="form-label">
                            <span className="label-text">Salary Type</span>
                            <span className="required">*</span>
                          </label>
                          <div className="select-wrapper">
                            <select
                              className="form-select"
                              value={manualFormData[0].salaryType}
                              onChange={(e) =>
                                handleManualChange("salaryType", e.target.value)
                              }
                            >
                              <option value="">Choose salary type</option>
                              <option value="EARNING">💰 Earning</option>
                              <option value="CONTRIBUTION">
                                ➖ Contribution
                              </option>
                            </select>
                          </div>
                        </div>

                        <div className="form-group">
                          <label className="form-label">
                            <span className="label-text">
                              Salary Component Name
                            </span>
                            <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <input
                              type="text"
                              className="form-input"
                              placeholder="e.g., Basic Salary, HRA, PF"
                              value={manualFormData[0].salaryName}
                              onChange={(e) =>
                                handleManualChange("salaryName", e.target.value)
                              }
                            />
                            <div className="input-decoration"></div>
                          </div>
                        </div>

                        <div className="form-group">
                          <label className="form-label">
                            <span className="label-text">
                              Contribution Type
                            </span>
                            <span className="required">*</span>
                          </label>
                          <div className="select-wrapper">
                            <select
                              className="form-select"
                              value={manualFormData[0].contributionType}
                              onChange={(e) =>
                                handleManualChange(
                                  "contributionType",
                                  e.target.value,
                                )
                              }
                            >
                              <option value="">Select contributor</option>
                              <option value="EMPLOYEE">👤 Employee</option>
                              <option value="EMPLOYER">🏢 Employer</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="auto-form-container">
                      <div className="form-grid three-column">
                        <div className="form-group">
                          <label className="form-label">
                            <span className="label-text">
                              Salary Component Name
                            </span>
                            <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <input
                              type="text"
                              name="salaryName"
                              className="form-input"
                              placeholder="e.g., Basic Salary, HRA, PF"
                              value={autoFormData.salaryName}
                              onChange={handleAutoChange}
                            />
                            <div className="input-decoration"></div>
                          </div>
                        </div>

                        <div className="form-group">
                          <label className="form-label">
                            <span className="label-text">Salary Type</span>
                            <span className="required">*</span>
                          </label>
                          <div className="select-wrapper">
                            <select
                              name="salaryType"
                              className="form-select"
                              value={autoFormData.salaryType}
                              onChange={handleAutoChange}
                            >
                              <option value="">Choose salary type</option>
                              <option value="EARNING">💰 Earning</option>
                              <option value="CONTRIBUTION">
                                ➖ Contribution
                              </option>
                            </select>
                          </div>
                        </div>

                        <div className="form-group">
                          <label className="form-label">
                            <span className="label-text">
                              Contribution Type
                            </span>
                            <span className="required">*</span>
                          </label>
                          <div className="select-wrapper">
                            <select
                              name="contributionType"
                              className="form-select"
                              value={autoFormData.contributionType}
                              onChange={handleAutoChange}
                            >
                              <option value="">Select contributor</option>
                              <option value="EMPLOYEE">👤 Employee</option>
                              <option value="EMPLOYER">🏢 Employer</option>
                            </select>
                          </div>
                        </div>

                        <div className="form-group">
                          <label className="form-label">
                            <span className="label-text">Value Amount</span>
                            <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <input
                              type="number"
                              name="value"
                              className="form-input"
                              placeholder={
                                autoFormData.taxType === "PT" ||
                                autoFormData.taxType === "IT"
                                  ? "Please configure this in the Professional Tax or Income Tax."
                                  : "Enter numerical value"
                              }
                              value={autoFormData.value}
                              onChange={handleAutoChange}
                              disabled={
                                autoFormData.taxType === "PT" ||
                                autoFormData.taxType === "IT"
                              }
                              style={
                                autoFormData.taxType === "PT" ||
                                autoFormData.taxType === "IT"
                                  ? {
                                      backgroundColor: "#f5f5f5",
                                      cursor: "not-allowed",
                                    }
                                  : {}
                              }
                            />
                            <div className="input-decoration"></div>
                          </div>
                        </div>

                        <div className="form-group">
                          <label className="form-label">
                            <span className="label-text">Value Type</span>
                            <span className="required">*</span>
                          </label>
                          <div className="select-wrapper">
                            <select
                              name="valueType"
                              className="form-select"
                              value={autoFormData.valueType}
                              onChange={handleAutoChange}
                              disabled={
                                autoFormData.taxType === "PT" ||
                                autoFormData.taxType === "IT"
                              }
                              style={
                                autoFormData.taxType === "PT" ||
                                autoFormData.taxType === "IT"
                                  ? {
                                      backgroundColor: "#f5f5f5",
                                      cursor: "not-allowed",
                                    }
                                  : {}
                              }
                            >
                              <option value="">
                                {autoFormData.taxType === "PT" ||
                                autoFormData.taxType === "IT"
                                  ? "Go to Professional Tax section Or Income Tax section to set these values"
                                  : "Select value type"}
                              </option>
                              <option value="PERCENTAGE">
                                📊 Percentage (%)
                              </option>
                              <option value="FIXED">💵 Fixed Amount</option>
                            </select>
                          </div>
                        </div>

                        <div className="form-group">
                          <label className="form-label">
                            <span className="label-text">Limit Amount</span>
                            <span className="optional">(Optional)</span>
                          </label>
                          <div className="input-wrapper">
                            <input
                              type="number"
                              name="limitAmount"
                              className="form-input"
                              placeholder="Maximum limit (optional)"
                              value={autoFormData.limitAmount}
                              onChange={handleAutoChange}
                            />
                            <div className="input-decoration"></div>
                          </div>
                        </div>

                        {autoFormData.valueType === "PERCENTAGE" && (
                          <>
                            <div className="form-group">
                              <label className="form-label">
                                <span className="label-text">
                                  Calculation Base
                                </span>
                              </label>
                              <div className="select-wrapper">
                                <select
                                  name="paymentCalculationBasedOn"
                                  className="form-select"
                                  value={autoFormData.paymentCalculationBasedOn}
                                  onChange={handleAutoChange}
                                >
                                  <option value="">
                                    Select calculation base
                                  </option>
                                  <option value="GROSS_SALARY">
                                    💼 Gross Salary
                                  </option>
                                  <option value="PAYMENT_SETUP">
                                    ⚙️ Payment Setup
                                  </option>
                                </select>
                              </div>
                            </div>

                            <div className="form-group">
                              <label className="form-label">
                                <span className="label-text">
                                  Predefined Setup
                                </span>
                              </label>
                              <div
                                className="multi-select-options"
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: "8px",
                                }}
                              >
                                {[
                                  ...new Map(
                                    predefinedSetups
                                      .filter(
                                        (setup) => setup.id !== editingSetupId,
                                      )
                                      .map((setup) => [
                                        setup.salaryName,
                                        setup,
                                      ]),
                                  ).values(),
                                ].map((setup) => {
                                  const checked = (
                                    autoFormData.paymentSetup || []
                                  ).includes(setup.salaryName);
                                  return (
                                    <label
                                      key={setup.id}
                                      className={`multi-select-chip ${
                                        checked ? "selected" : ""
                                      }`}
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "6px",
                                        padding: "6px 12px",
                                        borderRadius: "999px",
                                        border: `1px solid ${
                                          checked ? "#16a34a" : "#d1d5db"
                                        }`,
                                        background: checked
                                          ? "#dcfce7"
                                          : "transparent",
                                        cursor: "pointer",
                                        userSelect: "none",
                                      }}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() =>
                                          handleTogglePaymentSetup(
                                            setup.salaryName,
                                          )
                                        }
                                      />
                                      {setup.salaryName}
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                          </>
                        )}

                        {(autoFormData.salaryType === "CONTRIBUTION" ||
                          autoFormData.salaryType === "Contribution") && (
                          <div className="form-group">
                            <label className="form-label">
                              <span className="label-text">Tax Category</span>
                            </label>
                            <div className="select-wrapper">
                              <select
                                name="taxType"
                                className="form-select"
                                value={autoFormData.taxType}
                                onChange={handleAutoChange}
                              >
                                <option value="NIL">No Tax Applied</option>
                                <option value="IT">📋 Income Tax</option>
                                <option value="PT">👔 Professional Tax</option>
                                <option value="ESI">🏥 ESI Contribution</option>
                                <option value="PF">🏦 Provident Fund</option>
                              </select>
                            </div>
                          </div>
                        )}
                      </div>
                      {(autoFormData.salaryType === "EARNING" ||
                        autoFormData.salaryType === "Earning") &&
                        autoFormData.taxType !== "PT" && // Add this condition
                        (!hasRemainingEarningsSetup ||
                          (editingSetupId &&
                            predefinedSetups.find(
                              (s) => s.id === editingSetupId,
                            )?.remainingEarnings)) && (
                          <div className="checkbox-section">
                            <div className="checkbox-card">
                              <div className="checkbox-group">
                                <ColoredCheckbox
                                  type="checkbox"
                                  name="remainingEarnings"
                                  checked={autoFormData.remainingEarnings}
                                  onChange={(e) =>
                                    setAutoFormData((prev) => ({
                                      ...prev,
                                      remainingEarnings:
                                        typeof e?.target?.checked === "boolean"
                                          ? e.target.checked
                                          : !prev.remainingEarnings,
                                    }))
                                  }
                                  color="green"
                                />
                                <label className="checkbox-label">
                                  <div className="checkbox-content">
                                    <span className="checkbox-title">
                                      Remaining Earnings
                                    </span>
                                    <span className="checkbox-description">
                                      Apply this component to remaining salary
                                      after other calculations are completed
                                    </span>
                                  </div>
                                </label>
                              </div>
                            </div>
                          </div>
                        )}
                    </div>
                  )}

                  <div className="form-actions">
                    <div className="form-actions">
                      <ButtonWithIcon
                        type="button"
                        onClick={handleCancelForm}
                        className="cancel-button"
                        icon="ic:round-close"
                        text="Cancel"
                      />
                      <ButtonWithIcon
                        type="button" // Make sure this is "button" not "submit"
                        onClick={(e) => {
                          e.preventDefault(); // Prevent form submission
                          e.stopPropagation(); // Stop event bubbling
                          handleAddOrUpdatePaymentSetup(e);
                        }}
                        className="submit-button"
                        icon={
                          editingSetupId
                            ? "iconamoon:edit-light"
                            : "ic:round-plus"
                        }
                        text={editingSetupId ? "Update Setup" : "Add Setup"}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Section */}
            <div className="form-section">
              <div className="submit-section">
                <div className="submit-card">
                  <div className="submit-content">
                    <div className="submit-info">
                      <h4 className="submit-title">
                        Ready to Create Your Payroll?
                      </h4>
                      <p className="submit-description">
                        Review your configuration and finalize the payroll
                        setup. You can always modify these settings later.
                      </p>
                      <div className="submit-checklist">
                        <div className="checklist-item">
                          <span className="check-icon">✓</span>
                          <span>Basic configuration completed</span>
                        </div>
                        <div className="checklist-item">
                          <span className="check-icon">✓</span>
                          <span>Payment components configured</span>
                        </div>
                        <div className="checklist-item">
                          <span className="check-icon">✓</span>
                          <span>Template selected</span>
                        </div>
                      </div>
                    </div>
                    <div className="submit-actions">
                      <button type="submit" className="btn-submit">
                        <span className="btn-icon">🚀</span>
                        <span className="btn-text">Create Payroll Setup</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      )}

      <WarningPopup
        isOpen={deletePopup.isOpen}
        onClose={handleDeleteCancel}
        onAccept={handleDeleteConfirm}
        onDecline={handleDeleteCancel}
        title="Delete Payroll"
        message={`Are you sure you want to delete "${
          deletePopup.payrollToDelete?.payrollName || "this payroll"
        }"? This action cannot be undone.`}
        acceptText="Delete"
        declineText="Cancel"
      />
    </div>
  );
}

export default PaymentSetup;
